/**
 * B2B Marketplace Types - Enhanced Product Catalog
 * Type definitions for B2B marketplace with advanced product details
 */

import type { ProductCategory, ProductImage, ProductStatus } from './products.types';

/**
 * Product Grade
 */
export type ProductGrade = 'GRADE_A' | 'GRADE_B' | 'PREMIUM';

/**
 * Stock Status
 */
export type StockStatus = 'IN_STOCK' | 'LIMITED_STOCK' | 'OUT_OF_STOCK';

/**
 * Processing Unit (Origin)
 */
export interface ProcessingUnit {
  id: string;
  name: string;
  location: string;
}

/**
 * Certification
 */
export interface Certification {
  id: string;
  name: string;
  code?: string;
}

/**
 * B2B Product - Enhanced product with B2B-specific fields
 */
export interface B2BProduct {
  id: string;
  name: string;
  description: string;
  sku: string;
  category: ProductCategory;
  subcategory?: string;

  // Pricing
  priceMin: number;
  priceMax: number;
  unit: string;
  priceOnRequest: boolean;

  // MOQ & Lead Time
  moq: number;
  moqUnit: string;
  leadTimeDays: {
    min: number;
    max: number;
  };

  // Stock & Grade
  stockStatus: StockStatus;
  grade: ProductGrade;

  // Origin & Certifications
  processingUnit: ProcessingUnit;
  certifications: Certification[];

  // Ratings
  rating: number;
  reviewCount: number;

  // Images
  images: ProductImage[];
  headerColor: string;
  icon?: string;

  // Metadata
  status: ProductStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * B2B Product Card Props
 */
export interface B2BProductCardProps {
  product: B2BProduct;
  onGetQuote?: (product: B2BProduct) => void;
  className?: string;
}

/**
 * Filter Options
 */
export interface B2BFilterOptions {
  category: ProductCategory | null;
  grade: ProductGrade | null;
  certification: string | null;
  processingUnit: string | null;
  stockStatus: StockStatus | null;
  location: string | null;
  search?: string;
}

/**
 * Filter Bar Props
 */
export interface B2BFilterBarProps {
  filters: B2BFilterOptions;
  onFiltersChange: (filters: Partial<B2BFilterOptions>) => void;
  categories: { value: ProductCategory; label: string }[];
  grades: { value: ProductGrade; label: string }[];
  certifications: { value: string; label: string }[];
  processingUnits: { value: string; label: string }[];
  locations: { value: string; label: string }[];
  className?: string;
}

/**
 * Inquiry Form Data - Extended for B2B
 */
export interface B2BInquiryFormData {
  fullName: string;
  phone: string;
  email: string;
  location: string;
  quantity: number;
  additionalRequirements?: string;
  agreeToContact: boolean;
}

/**
 * Inquiry Modal Props
 */
export interface B2BInquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: B2BProduct | null;
  onSubmit: (data: B2BInquiryFormData) => Promise<string | undefined>;
}

/**
 * B2B Marketplace Page Props
 */
export interface B2BMarketplacePageProps {
  className?: string;
}

/**
 * Grade Configuration
 */
export const GRADE_CONFIG: Record<ProductGrade, { label: string; color: string; bgColor: string }> = {
  GRADE_A: {
    label: 'Grade A',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  GRADE_B: {
    label: 'Grade B',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  PREMIUM: {
    label: 'Premium',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
};

/**
 * Stock Status Configuration
 */
export const STOCK_STATUS_CONFIG: Record<StockStatus, { label: string; color: string; bgColor: string }> = {
  IN_STOCK: {
    label: 'In Stock',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  LIMITED_STOCK: {
    label: 'Limited Stock',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  OUT_OF_STOCK: {
    label: 'Out of Stock',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
};

/**
 * Header Colors for Product Cards
 */
export const PRODUCT_HEADER_COLORS = {
  green: 'bg-gradient-to-br from-green-400 to-green-600',
  blue: 'bg-gradient-to-br from-blue-400 to-blue-600',
  purple: 'bg-gradient-to-br from-purple-400 to-purple-600',
  orange: 'bg-gradient-to-br from-orange-400 to-orange-600',
  teal: 'bg-gradient-to-br from-teal-400 to-teal-600',
  amber: 'bg-gradient-to-br from-amber-400 to-amber-600',
} as const;
